export class CacheBusterUtils {
	/**
	 * Appends a cache-busting query parameter to a URL using Unix timestamp
	 * @param url The URL to add cache buster to
	 * @param seed
	 * @param paramName The query parameter name (default: 'cb')
	 * @param force Do we force a new cache buster, even if one already exists?
	 * @returns URL with cache buster appended
	 */
	static appendCacheBusterToUrl(
		url: string | undefined,
		seed?: number,
		paramName: string = 'cb',
		force: boolean = false
	): string | undefined {
		if (!url) {
			return url;
		}

		if (force) {
			url = this.removeCacheBusterFromUrl(url, paramName);
		}

		// Don't add cache buster to data URLs or URLs that already have the parameter
		if (url.startsWith('data:') || url.includes(`${paramName}=`)) {
			return url;
		}

		// If no seed was provided, generate our own using the current timestamp
		if (seed === undefined || seed === null) {
			seed = Date.now();
		}
		const separator = url.includes('?') ? '&' : '?';
		return `${url}${separator}${paramName}=${seed}`;
	}

	/**
	 * Adds cache buster to an asset object's assetPath property
	 * @param asset Asset object with assetPath
	 * @param force
	 * @returns Asset with cache-busted assetPath
	 */
	static addCacheBusterToAsset<T extends { assetPath?: string }>(asset: T, force: boolean = false): T {
		if (!asset || !asset.assetPath) {
			return asset;
		}

		return {
			...asset,
			assetPath: CacheBusterUtils.appendCacheBusterToUrl(asset.assetPath, undefined, 'cb', force)
		};
	}

	/**
	 * Removes a cache buster query parameter from a given URL.
	 *
	 * @param {string | undefined} url - The URL from which to remove the cache buster parameter. If undefined, the method returns undefined.
	 * @param {string} [paramName='cb'] - The name of the cache buster parameter to remove. Defaults to 'cb'.
	 * @return {string | undefined} The updated URL with the cache buster parameter removed, or the original URL if parsing fails or if the URL is undefined.
	 */
	static removeCacheBusterFromUrl(url: string | undefined, paramName: string = 'cb'): string | undefined {
		if (!url) {
			return url;
		}

		// Create URL object to safely manipulate query parameters
		try {
			const urlObj = new URL(url);

			// Remove the cache buster parameter
			urlObj.searchParams.delete(paramName);

			// Return the cleaned URL
			return urlObj.toString();
		} catch (error) {
			// If URL parsing fails, fall back to string manipulation
			const regex = new RegExp(`[?&]${paramName}=[^&]*(&|$)`, 'g');
			return url
				.replace(regex, (match, suffix) => {
					// If the parameter was the first one (?cb=...), replace ? with nothing
					// If it was later (&cb=...), handle the ampersand correctly
					if (match.startsWith('?')) {
						return suffix === '&' ? '?' : '';
					}
					return suffix === '&' ? '&' : '';
				})
				.replace(/[?&]$/, ''); // Clean up trailing ? or &
		}
	}

	/**
	 * Removes the cache buster parameter from the asset's path, if present.
	 *
	 * @param {T} asset - The asset object that may contain an `assetPath` property with a cache buster parameter.
	 * @return {T} The updated asset object with the `assetPath` property modified to exclude the cache buster, if applicable.
	 */
	static removeCacheBusterFromAsset<T extends { assetPath?: string }>(asset: T): T {
		if (!asset || !asset.assetPath) {
			return asset;
		}

		return {
			...asset,
			assetPath: CacheBusterUtils.removeCacheBusterFromUrl(asset.assetPath)
		};
	}
}
